export const copyFormattedContent = async (markdownContent: string, messageElement: HTMLElement): Promise<boolean> => {
  try {
    // Get the rendered HTML from the DOM element
    const rawHtml = messageElement.querySelector('.message-content-inner')?.innerHTML || messageElement.innerHTML;

    // Convert to email-compatible HTML with inline styles
    const htmlContent = convertToEmailHtml(rawHtml);

    // Convert markdown to clean plain text
    const plainText = convertMarkdownToPlainText(markdownContent);

    // Try to use the modern Clipboard API with multiple formats
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' })
      });

      await navigator.clipboard.write([clipboardItem]);
      return true;
    } else {
      // Fallback to plain text copy
      await navigator.clipboard.writeText(plainText);
      return true;
    }
  } catch (err) {
    console.error('Failed to copy formatted content:', err);

    // Final fallback - try plain text copy
    try {
      await navigator.clipboard.writeText(convertMarkdownToPlainText(markdownContent));
      return true;
    } catch (fallbackErr) {
      console.error('Failed to copy even plain text:', fallbackErr);
      return false;
    }
  }
};

// Convert HTML to email-compatible format with inline styles
const convertToEmailHtml = (html: string): string => {
  // Create a temporary container to parse and modify the HTML
  const container = document.createElement('div');
  container.innerHTML = html;

  // Remove citation elements (buttons/links with citation classes)
  container.querySelectorAll('[class*="citation"], button, .cite-button').forEach(el => el.remove());

  // Add inline styles to common elements for email compatibility
  container.querySelectorAll('strong, b').forEach(el => {
    (el as HTMLElement).style.fontWeight = 'bold';
  });

  container.querySelectorAll('em, i').forEach(el => {
    (el as HTMLElement).style.fontStyle = 'italic';
  });

  container.querySelectorAll('ul').forEach(el => {
    (el as HTMLElement).style.cssText = 'margin: 8px 0; padding-left: 24px; list-style-type: disc;';
  });

  container.querySelectorAll('ol').forEach(el => {
    (el as HTMLElement).style.cssText = 'margin: 8px 0; padding-left: 24px; list-style-type: decimal;';
  });

  container.querySelectorAll('li').forEach(el => {
    (el as HTMLElement).style.cssText = 'margin: 4px 0;';
  });

  container.querySelectorAll('p').forEach(el => {
    (el as HTMLElement).style.cssText = 'margin: 8px 0;';
  });

  container.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
    const level = parseInt(el.tagName[1]);
    const fontSize = Math.max(24 - (level * 2), 14);
    (el as HTMLElement).style.cssText = `font-size: ${fontSize}px; font-weight: bold; margin: 12px 0 8px 0;`;
  });

  container.querySelectorAll('code').forEach(el => {
    (el as HTMLElement).style.cssText = 'background-color: #f4f4f4; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 90%;';
  });

  container.querySelectorAll('pre').forEach(el => {
    (el as HTMLElement).style.cssText = 'background-color: #f4f4f4; padding: 12px; border-radius: 6px; overflow-x: auto; margin: 8px 0;';
  });

  container.querySelectorAll('blockquote').forEach(el => {
    (el as HTMLElement).style.cssText = 'border-left: 4px solid #ddd; margin: 8px 0; padding-left: 16px; color: #666;';
  });

  container.querySelectorAll('table').forEach(el => {
    (el as HTMLElement).style.cssText = 'border-collapse: collapse; margin: 8px 0; width: 100%;';
  });

  container.querySelectorAll('th, td').forEach(el => {
    (el as HTMLElement).style.cssText = 'border: 1px solid #ddd; padding: 8px; text-align: left;';
  });

  container.querySelectorAll('th').forEach(el => {
    (el as HTMLElement).style.backgroundColor = '#f4f4f4';
    (el as HTMLElement).style.fontWeight = 'bold';
  });

  // Return the modified HTML
  return container.innerHTML;
};

export const convertMarkdownToPlainText = (markdown: string): string => {
  return markdown
    // Remove headers but keep text
    .replace(/^#{1,6}\s+(.+)$/gm, '$1')
    // Remove bold
    .replace(/\*\*(.+?)\*\*/g, '$1')
    // Remove italic
    .replace(/\*(.+?)\*/g, '$1')
    // Remove underscores for italic/bold
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // Handle code blocks - preserve content but remove backticks
    .replace(/```[\s\S]*?```/g, (match) => {
      return match
        .replace(/^```\w*\n?/gm, '')
        .replace(/```$/gm, '')
        .trim();
    })
    // Remove inline code backticks
    .replace(/`(.+?)`/g, '$1')
    // Convert links to just the text
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    // Convert images to alt text
    .replace(/!\[(.+?)\]\(.+?\)/g, '[$1]')
    // Remove horizontal rules
    .replace(/^---+$/gm, '')
    .replace(/^\*\*\*+$/gm, '')
    // Handle lists - preserve structure
    .replace(/^[\s]*[-*+]\s+(.+)$/gm, '• $1')
    .replace(/^[\s]*\d+\.\s+(.+)$/gm, '$1')
    // Handle blockquotes
    .replace(/^>\s+(.+)$/gm, '$1')
    // Clean up tables (basic)
    .replace(/\|/g, ' ')
    .replace(/^[-\s]+$/gm, '')
    // Remove multiple blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Helper function to copy just plain text
export const copyPlainText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text:', err);
    return false;
  }
};